import React from 'react';
import {Grid, Typography} from '@mui/material';
import {
  PatientEncounter,
} from '../../../patient/components/models/patient-encounter';
import {formatNumberToUSD, getOrdinalSuffix} from '../../../shared/calculators';
import {
  AuthDocModal, installment, singlePaymentAgreement,
} from '../../../shared/components/auth-doc-modal/auth-doc-modal';
import {
  AuthDocPdfModal,
} from '../../../shared/components/auth-doc-modal/auth-doc-pdf-modal';
import _ from 'lodash';

export const paymentAgreementItems = [
  'Financial Responsibility and Assignment of Benefits',
  'Payment Authorization Terms and Conditions',
  'Installment Agreement',
  'Federal Truth-In-Lending Disclosure Statement',
];

export type NoSigDynamicContentModalState = {
  state: boolean;
  setState: React.Dispatch<React.SetStateAction<any>>;
};

export type GetNoSigDynamicContentParams = {
  typeOfDoc: 'inst' | 'spa' | 'otp' | 'phi';
  patientEncounter: PatientEncounter;
  modalState: NoSigDynamicContentModalState;
  pdfBlob: Blob | null;
  payment?: Record<string, any> | null;
};

export const getDynamicContent = ({
  typeOfDoc,
  patientEncounter,
  modalState,
  pdfBlob,
  payment,
}: GetNoSigDynamicContentParams) => {

  const estimatedPFR = (
      <>
        <Grid item xs={9}>
          <Typography variant="subtitle2">
            Estimated Patient <br/>
            Financial Responsibility (PFR):
          </Typography>
        </Grid>
        <Grid item xs={3} container justifyContent="flex-start">
          <Typography
              variant="subtitle2"
              align="right"
              test-id="page-pfr-amt"
          >
            {formatNumberToUSD(
                patientEncounter.adjustedPfrAmt)}
          </Typography>
        </Grid>
      </>
  );

  const getPaidToDate = () => (
      <>
        <Grid item xs={9}>
          <Typography variant="subtitle2">
            {`Paid to Date${typeOfDoc==='otp' ? '*' : ""}`} :
          </Typography>
        </Grid>
        <Grid item xs={3} container justifyContent="flex-start">
          <Typography
              variant="subtitle2"
              align="right"
              test-id="paid-to-date"
          >
            {formatNumberToUSD(
                patientEncounter.adjustedPfrAmt! - patientEncounter.patientPaymentProgram?.[0]?.patientPaymentSchedule?.pfrCapturedBalance!)}
          </Typography>
        </Grid>
      </>
  );

  const getEstRemainingBalance = () => (
      <>
        <Grid item xs={9}>
          <Typography variant="subtitle2">
            Estimated Remaining Balance:
          </Typography>
        </Grid>
        <Grid item xs={3} container justifyContent="flex-start">
          <Typography
              variant="subtitle2"
              align="right"
              test-id="est-remaining-balance"
          >
            {formatNumberToUSD(patientEncounter.patientPaymentProgram?.[0]?.patientPaymentSchedule?.pfrCapturedBalance)}
          </Typography>
        </Grid>
      </>
  );

  const getPendingPayments = () => (
      <>
        <Grid item xs={9}>
          <Typography variant="subtitle2">
            Pending Payments:
          </Typography>
        </Grid>
        <Grid item xs={3} container justifyContent="flex-start">
          <Typography
              variant="subtitle2"
              align="right"
              test-id="pending-payments"
          >
            {formatNumberToUSD(patientEncounter?.patientPaymentProgram?.[0]?.patientPaymentSchedule?.pfrCapturedBalance! - patientEncounter?.patientPaymentProgram?.[0]?.patientPaymentSchedule?.pfrPendingBalance!)}
          </Typography>
        </Grid>
      </>
  );

  switch (typeOfDoc) {
    case 'inst':
      return {
        programDetails: (
            <>

              {estimatedPFR}
              {getPaidToDate()}
              {getEstRemainingBalance()}

              <Grid item xs={9}>
                <Typography
                    variant="subtitle2"
                >
                  Interest Rate:
                </Typography>
              </Grid>
              <Grid item xs={3} container justifyContent="flex-start">
                <Typography
                    variant="subtitle2"
                    align="right"
                    test-id="interest-rate"
                >
                  0%
                </Typography>
              </Grid>

              <Grid item xs={9}>
                <Typography variant="subtitle2">
                  Plan Type:
                </Typography>
              </Grid>
              <Grid item xs={3} container justifyContent="flex-start">
                <Typography
                    variant="subtitle2"
                    align="right"
                    test-id="plan-type"
                >
                  {patientEncounter.patientPaymentProgram?.[0]?.patientPaymentSchedule?.paymentFreq.toUpperCase() === 'M' ? 'Monthly' : 'Weekly'}
                </Typography>
              </Grid>

              <Grid item xs={9}>
                <Typography variant="subtitle2">
                  Term Length:
                </Typography>
              </Grid>
              <Grid item xs={3} container justifyContent="flex-start">
                <Typography
                    variant="subtitle2"
                    align="right"
                    test-id="term-length"
                >
                  {patientEncounter.patientPaymentProgram?.[0]?.patientPaymentSchedule?.terms}
                </Typography>
              </Grid>

              <Grid item xs={9}>
                <Typography variant="subtitle2">
                  Recurring Payment Amount:
                </Typography>
              </Grid>
              <Grid item xs={3} container justifyContent="flex-start">
                <Typography
                    variant="subtitle2"
                    align="right"
                    test-id="recurring-payment-amt"
                >
                  {formatNumberToUSD(patientEncounter?.patientPaymentProgram?.[0]?.patientPaymentSchedule?.paymentDueAmt)}
                </Typography>
              </Grid>

              <Grid item xs={9}>
                <Typography variant="subtitle2">
                  Recurring Payment Due Date:
                </Typography>
              </Grid>
              <Grid item xs={3} container justifyContent="flex-start">
                <Typography
                    variant="subtitle2"
                    align="right"
                    test-id="recurring-payment-due-date"
                >
                  {(() => {
                    const paymentSchedule = patientEncounter?.patientPaymentProgram?.[0]?.patientPaymentSchedule;
                    const paymentStartDay = +paymentSchedule?.paymentStartDay;
                    const scheduleStartDate = paymentSchedule?.scheduleStartDt;
                    if (paymentSchedule?.paymentFreq === 'M' && paymentStartDay > 0) {
                      return (
                          `${paymentStartDay}${getOrdinalSuffix(paymentStartDay)}`
                      );
                    } else if (paymentSchedule?.paymentFreq === 'W' && scheduleStartDate) {
                      const date = new Date(scheduleStartDate).getUTCDay();
                      return ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'][date];
                    }
                  })()}
                </Typography>
              </Grid>

              {getPendingPayments()}

            </>
        ),
        programAgreementDetails: (
            <>
              <li>{paymentAgreementItems[0]}</li>
              <li>{paymentAgreementItems[1]}</li>
              <li>{paymentAgreementItems[2]}</li>
              <li>{paymentAgreementItems[3]}</li>
            </>
        ),
        docModal: (
            pdfBlob ? (
                <AuthDocPdfModal
                    isOpen={modalState.state}
                    handleModalCancel={() => {
                      modalState.setState(false);
                    }}
                    pdfBlob={pdfBlob}
                />
                ) : (
                <AuthDocModal
                    isOpen={modalState.state}
                    handleModalCancel={() => {
                      modalState.setState(false);
                    }}
                    authDocItems={installment}
                />
                )
        ),
      };
    case 'spa':
      return {
        programDetails: (
          <>
            {estimatedPFR}
            {getPaidToDate()}
            {getEstRemainingBalance()}
            {getPendingPayments()}
          </>
        ),
        programAgreementDetails: (
          <>
            <li>{paymentAgreementItems[0]}</li>
            <li>{paymentAgreementItems[1]}</li>
          </>
        ),
        docModal: (

            pdfBlob ? (
                <AuthDocPdfModal
                    isOpen={modalState.state}
                    handleModalCancel={() => {
                      modalState.setState(false);
                    }}
                    pdfBlob={pdfBlob}
                />
            ) : (
                <AuthDocModal
                    isOpen={modalState.state}
                    handleModalCancel={() => {
                      modalState.setState(false);
                    }}
                    authDocItems={singlePaymentAgreement}
                />
                )

        ),
      };
    case 'otp': {
      if (!payment) {
        throw Error('Cannot render payment agreement content because payment record missing')
      }

      const isPaymentPending = payment.ledger.ledgerTypeId === 4;

      const paymentAmt = (
          <>
            <Grid item xs={9}>
              <Typography
                  variant="subtitle2"
                  test-id="page-payment-amt-text"
              >
                {(isPaymentPending ? 'Pending ' : '') + 'Payment Amount :'}
              </Typography>
            </Grid>
            <Grid item xs={3} container justifyContent="flex-start">
              <Typography
                  variant="subtitle2"
                  align="right"
                  test-id="page-payment-amt"
              >
                {formatNumberToUSD(
                    payment.paymentAmt)}
              </Typography>
            </Grid>
          </>
      );

      return {
        paymentDetails: (
            <>
              {paymentAmt}
              {getPaidToDate()}
            </>
        ),
        docModal: (
                <AuthDocPdfModal
                    isOpen={modalState.state}
                    handleModalCancel={() => {
                      modalState.setState(false);
                    }}
                    pdfBlob={pdfBlob!}
                />
        ),
      };
    }
    case 'phi': {
      const champions = patientEncounter.patientChampion ?? [];

      return {
        phiDetails: (
            <>
              {
                  champions.map((champion, index) => {
                    const name = [
                      _.capitalize(champion?.contact?.firstName),
                      _.capitalize(champion?.contact?.lastName),
                    ].filter(Boolean).join(' ');
                    const phone = champion?.contact?.primPhoneNum || '';
                    const relationship = _.capitalize(champion?.relationshipToPatient) || '';
                    const championSummary = [name, phone, relationship].filter(
                        Boolean).join(', ');

                    return (
                        <Grid item xs={12} key={champion?.patientChampionId || index}>
                          <Typography
                              variant="subtitle2"
                              test-id={`phi-champion-${index}`}
                          >
                            {championSummary}
                          </Typography>
                        </Grid>
                    );
                  })
              }
            </>
        ),
        docModal: (
            <AuthDocPdfModal
                isOpen={modalState.state}
                handleModalCancel={() => {
                  modalState.setState(false);
                }}
                pdfBlob={pdfBlob!}
            />
        ),
      };
    }
    default:
      throw Error('Document type not supported')
  }
};
